// SSRF false positive: fetch host is getBaseApiUrl() (build-time config);
// only a validated own-session id is interpolated into the path, never the origin.
// fallow-ignore-file security-sink
import { jwtDecode } from 'jwt-decode'
import { globalUtils } from '@gat-ship/utils'
import { API_REQUEST_HEADERS } from '../api-request-headers'
import type { HttpError } from '../http-client-types'
import { parseErrorBody } from '../core/http-client'
import { getBaseApiUrl } from './gs-cloud-backend-utils'

function isSupportSessionTokenExpired(token: Readonly<string>) {
  let decodedToken: { iat: number; exp: number }
  try {
    decodedToken = jwtDecode<{ iat: number; exp: number }>(token)
  } catch {
    // Malformed / non-JWT value in sessionStorage (e.g. manually tampered
    // or an older encoding scheme). Treat as "expired" so the caller
    // re-fetches a fresh token instead of throwing on every subsequent
    // request — before this guard a garbage token would lock the
    // middleware until the user cleared storage by hand.
    if (import.meta.env.DEV) {
      console.log('support session token is malformed; triggering refresh')
    }
    return true
  }
  const { exp } = decodedToken
  const expiresInSeconds = exp - Math.floor(Date.now() / 1000)
  if (import.meta.env.DEV) {
    console.log(`support session token expires in ${expiresInSeconds} seconds`)
  }
  return expiresInSeconds <= 30
}

/**
 * Returns a valid support-session token, refreshing from the backend if the
 * current one is missing or within 30 seconds of expiry.
 *
 * This endpoint is a bootstrap call — it can't go through `createHttpClient`
 * without a chicken-and-egg cycle (the auth middleware would try to fetch a
 * support-session token to attach to the request that's fetching the
 * support-session token). We shape the thrown error to match `HttpError` so
 * `mapToAppError` can surface status / ProblemDetails the same way it does
 * for every other request.
 *
 * Takes storage accessors as parameters (rather than closing over VueUse
 * refs) so this file has no dependency on `lib/support-session`. That keeps
 * the dependency graph unidirectional: `http-client` depends on
 * `support-session` only for storage-level access in `gs-cloud-backend-
 * provider.vue`, not for token-refresh logic.
 */
export async function fetchSupportSessionTokenIfExpired(params: {
  supportSessionId: string | null
  currentToken: string | null
  authorizationToken: string
  setSupportSessionToken: (token: string | null) => void
}): Promise<string> {
  const {
    supportSessionId,
    currentToken,
    authorizationToken,
    setSupportSessionToken
  } = params

  if (!supportSessionId || typeof supportSessionId !== 'string') {
    throw new Error('No support session ID found in storage.')
  }

  if (currentToken && !isSupportSessionTokenExpired(currentToken)) {
    return currentToken
  }

  const response = await fetch(
    `${getBaseApiUrl()}support/sessions/${supportSessionId}/token/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [API_REQUEST_HEADERS.GATSHIP_COMMAND_ID]: globalUtils.functions.uuid(),
        [API_REQUEST_HEADERS.AUTHORIZATION]: `Bearer ${authorizationToken}`
      }
    }
  )

  if (!response.ok) {
    const httpError: HttpError = {
      status: response.status,
      data: await parseErrorBody(response),
      message: response.statusText || `HTTP ${response.status}`
    }
    throw httpError
  }

  const data = (await response.json()) as {
    expiresIn: number
    token: string
  }

  if (data && typeof data.token === 'string') {
    setSupportSessionToken(data.token)
    return data.token
  }
  throw new Error('Support session token response missing token field')
}
